"use client";

/**
 * RestaurantGallery
 * Mirrors StayProfileHeroGallery exactly:
 * - Full-width 16/9 hero image with gradient overlay and photo count pill
 * - Dialog carousel with prev/next arrows, swipe support, thumbnail strip
 * - Side-by-side video panel for celebration/venue showcase footage
 */

import { useState, useRef, useEffect } from "react";
import Image from "next/image";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Images, ChevronLeft, ChevronRight } from "lucide-react";

interface RestaurantGalleryProps {
  businessName: string;
  photoUrls: string[];
  celebrationVideoUrl?: string | null;
}

const dedupeUrls = (urls: string[]) => {
  const seen = new Set<string>();
  return urls.map((u) => u.trim()).filter(Boolean).filter((u) => {
    if (seen.has(u)) return false;
    seen.add(u);
    return true;
  });
};

export function RestaurantGallery({
  businessName,
  photoUrls,
  celebrationVideoUrl,
}: RestaurantGalleryProps) {
  const photos = dedupeUrls(photoUrls);
  const hasVideo = typeof celebrationVideoUrl === "string" && celebrationVideoUrl.trim().length > 0;

  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const touchStartXRef = useRef<number | null>(null);

  if (photos.length === 0 && !hasVideo) return null;

  const openGalleryAt = (index: number) => {
    setActiveIndex(index);
    setOpen(true);
  };

  const showNext = () => setActiveIndex((i) => (i + 1) % photos.length);
  const showPrev = () => setActiveIndex((i) => (i - 1 + photos.length) % photos.length);

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    touchStartXRef.current = e.changedTouches[0]?.clientX ?? null;
  };
  const handleTouchEnd = (e: React.TouchEvent<HTMLDivElement>) => {
    const startX = touchStartXRef.current;
    const endX = e.changedTouches[0]?.clientX;
    touchStartXRef.current = null;
    if (startX === null || typeof endX !== "number") return;
    const delta = endX - startX;
    if (Math.abs(delta) < 40) return;
    if (delta < 0) showNext(); else showPrev();
  };

  return (
    <>
      {/* ── Full-width hero image (same card shape as Stay) ─────────────── */}
      {photos.length > 0 && (
        <Card className="mt-8 overflow-hidden border-slate-200">
          <CardContent className="p-0">
            <button
              type="button"
              className="group relative block aspect-[16/9] w-full"
              onClick={() => openGalleryAt(0)}
              aria-label={`Open ${businessName} photo gallery`}
            >
              <Image
                src={photos[0]}
                alt={`${businessName} dining photo`}
                fill
                priority
                sizes="(max-width: 768px) 100vw, 1200px"
                className="object-cover transition duration-300 group-hover:scale-[1.02]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
              <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-white/90">Dining Gallery</p>
                  <p className="text-lg font-semibold text-white">Explore {businessName}</p>
                </div>
                <div className="inline-flex items-center rounded-full bg-black/50 px-3 py-1 text-sm font-medium text-white backdrop-blur-sm">
                  <Images className="mr-2 h-4 w-4" />
                  View {photos.length} photo{photos.length === 1 ? "" : "s"}
                </div>
              </div>
            </button>
            <div className="flex justify-end border-t bg-white px-4 py-3">
              <Button type="button" onClick={() => openGalleryAt(0)} variant="outline">
                <Images className="mr-2 h-4 w-4" />
                View more photos
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Celebration / venue video (same card shape as Stay walkthrough) */}
      {hasVideo && (
        <Card className="mt-4 overflow-hidden border-orange-100 bg-gradient-to-br from-white via-slate-50 to-orange-50/30 shadow-sm">
          <CardContent className="p-0">
            <div className="grid gap-0 md:grid-cols-[1.3fr_1fr]">
              <div className="relative aspect-video bg-black">
                <video
                  className="h-full w-full object-cover"
                  controls
                  playsInline
                  preload="metadata"
                  poster={photos[0] || undefined}
                >
                  <source src={celebrationVideoUrl!.trim()} />
                  Your browser does not support the video tag.
                </video>
              </div>
              <div className="flex flex-col justify-center gap-3 p-5">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-orange-600">Venue showcase</p>
                  <h3 className="mt-1 text-lg font-semibold text-slate-900">See the venue before you book</h3>
                  <p className="mt-2 text-sm text-slate-600">
                    A short video lets diners see the atmosphere, events, and celebrations before they visit.
                  </p>
                </div>
                <div className="rounded-lg border border-orange-100 bg-white/80 px-3 py-2 text-xs text-slate-600">
                  Updated regularly by the owner to showcase events and occasions.
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Dialog carousel — identical structure to Stay ───────────────── */}
      <Dialog open={open} onOpenChange={setOpen}>
        {open ? (
          <DialogContent className="max-w-5xl p-0 overflow-hidden flex max-h-[92dvh] flex-col">
            <DialogHeader className="border-b px-5 py-4 shrink-0">
              <DialogTitle>{businessName} Photo Gallery</DialogTitle>
              <DialogDescription>
                Swipe or use arrows to browse the full dining gallery.
              </DialogDescription>
            </DialogHeader>

            <div
              className="relative aspect-[4/3] sm:aspect-[16/10] max-h-[56dvh] w-full shrink-0 bg-black"
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
            >
              <Image
                src={photos[activeIndex]}
                alt={`${businessName} photo ${activeIndex + 1}`}
                fill
                sizes="90vw"
                className="object-contain"
              />
              {photos.length > 1 && (
                <>
                  <Button
                    type="button"
                    variant="secondary"
                    size="icon"
                    className="absolute left-3 top-1/2 -translate-y-1/2"
                    onClick={showPrev}
                    aria-label="Previous photo"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    size="icon"
                    className="absolute right-3 top-1/2 -translate-y-1/2"
                    onClick={showNext}
                    aria-label="Next photo"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </Button>
                </>
              )}
            </div>

            {photos.length > 1 && (
              <div className="max-h-28 shrink-0 overflow-x-auto border-t bg-white p-3">
                <div className="flex gap-2">
                  {photos.map((url, index) => (
                    <button
                      key={`${url}-${index}`}
                      type="button"
                      onClick={() => setActiveIndex(index)}
                      className={`relative h-16 w-24 shrink-0 overflow-hidden rounded-md border ${
                        index === activeIndex ? "border-orange-500" : "border-transparent"
                      }`}
                      aria-label={`View photo ${index + 1}`}
                    >
                      <Image
                        src={url}
                        alt={`Thumbnail ${index + 1}`}
                        fill
                        sizes="96px"
                        className="object-cover"
                      />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </DialogContent>
        ) : null}
      </Dialog>
    </>
  );
}
